// src/pages/AllBrokerReferral.jsx
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import AppConst from "../apis/appconst";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import Select from "react-select";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const AllBrokerReferral = () => {
  const getCurrentDate = () => new Date().toISOString().split("T")[0];

  const [brokerOptions, setBrokerOptions] = useState([]);
  const [loadingBrokers, setLoadingBrokers] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    search_key: "",
    from: getCurrentDate(),
    to: getCurrentDate(),
    broker_id: "",
    broker_name: "",
    labRecords: [],
    ipdRecords: [],
    opdRecords: [],
    pharmacyRecords: [],
    brokers: [],
  });

  const [payModalOpen, setPayModalOpen] = useState(false);
  const [payContext, setPayContext] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payProcessing, setPayProcessing] = useState(false);
  const [payError, setPayError] = useState("");

  // -------- helpers ----------
  const num = (v) => {
    const n = parseFloat(v ?? 0);
    return Number.isNaN(n) ? 0 : n;
  };

  const formatDate = (s) => {
    if (!s) return "";
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return "";
    return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
  };

  const readLabCommissions = (r) => {
    const commission = num(r.lab_reg_broker_commission ?? r.broker_commission);
    let paid = num(r.lab_reg_broker_commission_paid ?? r.broker_commission_paid);
    if (r.broker_payments_sum != null) paid = Math.max(paid, num(r.broker_payments_sum));
    return { commission, paid, due: Math.max(commission - paid, 0) };
  };
  const readIpdCommissions = (r) => {
    const commission = num(
      r.ipd_bill_broker_commission ?? r.ipd_bill_broker_commission_amount ?? r.broker_commission
    );
    let paid = num(
      r.ipd_bill_broker_commission_paid ?? r.ipd_broker_commission_paid ?? r.broker_commission_paid
    );
    if (r.broker_payments_sum != null) paid = Math.max(paid, num(r.broker_payments_sum));
    return { commission, paid, due: Math.max(commission - paid, 0) };
  };
  const readOpdCommissions = (r) => {
    const commission = num(r.opd_broker_commission ?? r.broker_commission);
    let paid = num(r.opd_broker_commission_paid ?? r.broker_commission_paid);
    if (r.broker_payments_sum != null) paid = Math.max(paid, num(r.broker_payments_sum));
    return { commission, paid, due: Math.max(commission - paid, 0) };
  };
  const readPharmCommissions = (r) => {
    const commission = num(r.med_bill_broker_commission ?? r.broker_commission);
    let paid = num(r.med_bill_broker_commission_paid ?? r.broker_commission_paid);
    if (r.broker_payments_sum != null) paid = Math.max(paid, num(r.broker_payments_sum));
    return { commission, paid, due: Math.max(commission - paid, 0) };
  };

  // ---------- load brokers ----------
  const loadBrokers = async (search = "") => {
    try {
      setLoadingBrokers(true);
      const fd = new FormData();
      fd.append("search_key", search || "");
      fd.append("page_no", "none");
      const res = await axios.post(
        AppConst.BASE_URL + AppConst.getBrokerListUrl,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const records = Array.isArray(res?.data?.records) ? res.data.records : [];

      setFormData((prev) => ({ ...prev, brokers: records }));

      const opts = records.map((r) => {
        const id = String(r.broker_id ?? r.id ?? r.user_id ?? "");
        const name =
          `${(r.fname || "").trim()}${r.lname ? " " + (r.lname || "").trim() : ""}`.trim() ||
          (r.mobile || "Unknown");
        const mobile = r.mobile ? ` — ${r.mobile}` : "";
        return { value: id, label: `${name}${mobile}`, raw: r };
      });
      setBrokerOptions(opts);
    } catch (err) {
      console.error("loadBrokers", err);
      setBrokerOptions([]);
    } finally {
      setLoadingBrokers(false);
    }
  };

  useEffect(() => {
    loadBrokers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- fetch all department records ----------
  const buildBaseForm = () => {
    const fd = new FormData();
    fd.append("search_key", formData.search_key || "");
    if (formData.from) fd.append("from", `${formData.from} 00:00:00`);
    if (formData.to) fd.append("to", `${formData.to} 23:59:59`);
    fd.append("page_no", "none");
    return fd;
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const fdLab = buildBaseForm();
      if (formData.broker_id) {
        fdLab.append("lab_reg_broker_id", String(formData.broker_id));
        fdLab.append("lab_reg_broker_name", formData.broker_name || "");
      }
      fdLab.append("department_filter", "all");

      const fdIpd = buildBaseForm();
      if (formData.broker_id) {
        fdIpd.append("ipd_bill_broker_id", String(formData.broker_id));
        fdIpd.append("ipd_bill_broker_name", formData.broker_name || "");
      }

      const fdOpd = buildBaseForm();
      if (formData.broker_id) {
        fdOpd.append("opd_broker_id", String(formData.broker_id));
        fdOpd.append("opd_broker_name", formData.broker_name || "");
      }

      const fdPharm = buildBaseForm();
      if (formData.broker_id) {
        fdPharm.append("med_bill_broker_id", String(formData.broker_id));
        fdPharm.append("med_bill_broker_name", formData.broker_name || "");
      }

      const [labRes, ipdRes, opdRes, pharmRes] = await Promise.allSettled([
        axios.post(AppConst.BASE_URL + AppConst.getDoctorReferralLabUrl, fdLab, {
          headers: { "Content-Type": "multipart/form-data" },
        }),
        AppConst.getBrokerReferralIpdUrl
          ? axios.post(AppConst.BASE_URL + AppConst.getBrokerReferralIpdUrl, fdIpd, {
              headers: { "Content-Type": "multipart/form-data" },
            })
          : Promise.resolve({ data: { status: true, records: [] } }),
        axios.post(AppConst.BASE_URL + AppConst.getBrokerReferralOpdUrl, fdOpd, {
          headers: { "Content-Type": "multipart/form-data" },
        }),
        axios.post(AppConst.BASE_URL + AppConst.getBrokerReferralPharmacyUrl, fdPharm, {
          headers: { "Content-Type": "multipart/form-data" },
        }),
      ]);

      const pick = (res) =>
        res.status === "fulfilled" && res.value?.data?.status
          ? res.value.data.records || []
          : [];

      setFormData((prev) => ({
        ...prev,
        labRecords: pick(labRes),
        ipdRecords: pick(ipdRes),
        opdRecords: pick(opdRes),
        pharmacyRecords: pick(pharmRes),
      }));
    } catch (err) {
      console.error("fetchAll", err);
      toast.error("Failed to fetch some department data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.from, formData.to, formData.broker_id, formData.search_key]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    setFormData((prev) => ({
      ...prev,
      search_key: "",
      from: getCurrentDate(),
      to: getCurrentDate(),
      broker_id: "",
      broker_name: "",
    }));
  };

  const brokerMetaById = useMemo(() => {
    const map = new Map();
    (formData.brokers || []).forEach((b) => {
      const id = String(b.broker_id ?? b.id ?? b.user_id ?? "");
      if (!id) return;
      map.set(id, {
        created_at: b.created_at || b.updated_at || "",
        broker_id_num: Number(b.broker_id ?? b.id ?? b.user_id ?? 0) || 0,
      });
    });
    return map;
  }, [formData.brokers]);

  // ---------- aggregate per broker ----------
  const aggregated = useMemo(() => {
    const map = new Map();

    const ensure = (id, name, mobile) => {
      const key = String(id || `name_${name || "unknown"}`);
      if (!map.has(key)) {
        map.set(key, {
          broker_id: id || "",
          broker_name: name || "Unknown",
          broker_mobile: mobile || "",
          lab: { commission: 0, paid: 0, due: 0, rows: [] },
          ipd: { commission: 0, paid: 0, due: 0, rows: [] },
          opd: { commission: 0, paid: 0, due: 0, rows: [] },
          pharmacy: { commission: 0, paid: 0, due: 0, rows: [] },
        });
      }
      return map.get(key);
    };

    (formData.brokers || []).forEach((b) => {
      const id = b.broker_id ?? b.id ?? b.user_id ?? "";
      const name =
        `${(b.fname || "").trim()}${b.lname ? " " + (b.lname || "").trim() : ""}`.trim() ||
        (b.mobile || "Unknown");
      ensure(id, name, b.mobile || "");
    });

    (formData.labRecords || []).forEach((r) => {
      const c = readLabCommissions(r);
      if (c.commission <= 0 && !r.lab_reg_broker_id) return;
      const entry = ensure(r.lab_reg_broker_id, r.lab_reg_broker_name, r.broker_mobile);
      entry.lab.commission += c.commission;
      entry.lab.paid += c.paid;
      entry.lab.due += c.due;
      entry.lab.rows.push(r);
    });
    (formData.ipdRecords || []).forEach((r) => {
      const c = readIpdCommissions(r);
      if (c.commission <= 0 && !r.ipd_bill_broker_id) return;
      const entry = ensure(r.ipd_bill_broker_id, r.ipd_bill_broker_name, r.broker_mobile);
      entry.ipd.commission += c.commission;
      entry.ipd.paid += c.paid;
      entry.ipd.due += c.due;
      entry.ipd.rows.push(r);
    });
    (formData.opdRecords || []).forEach((r) => {
      const c = readOpdCommissions(r);
      if (c.commission <= 0 && !r.opd_broker_id) return;
      const entry = ensure(r.opd_broker_id, r.opd_broker_name, r.broker_mobile);
      entry.opd.commission += c.commission;
      entry.opd.paid += c.paid;
      entry.opd.due += c.due;
      entry.opd.rows.push(r);
    });
    (formData.pharmacyRecords || []).forEach((r) => {
      const c = readPharmCommissions(r);
      if (c.commission <= 0 && !r.med_bill_broker_id) return;
      const entry = ensure(r.med_bill_broker_id, r.med_bill_broker_name, r.broker_mobile);
      entry.pharmacy.commission += c.commission;
      entry.pharmacy.paid += c.paid;
      entry.pharmacy.due += c.due;
      entry.pharmacy.rows.push(r);
    });

    let arr = Array.from(map.values()).filter(
      (b) =>
        b.lab.commission > 0 ||
        b.ipd.commission > 0 ||
        b.opd.commission > 0 ||
        b.pharmacy.commission > 0
    );

    if (formData.broker_id) {
      arr = arr.filter((b) => String(b.broker_id) === String(formData.broker_id));
    }

    if (formData.search_key && formData.search_key.trim() !== "") {
      const sk = formData.search_key.trim().toLowerCase();
      arr = arr.filter(
        (b) =>
          (b.broker_name || "").toLowerCase().includes(sk) ||
          (b.broker_mobile || "").toLowerCase().includes(sk)
      );
    }

    arr.forEach((b) => {
      const meta = brokerMetaById.get(String(b.broker_id));
      b._created_at = meta?.created_at || "";
      b._broker_id_num = meta?.broker_id_num || Number(b.broker_id) || 0;
    });

    arr.sort((a, b) => {
      const ta = a._created_at ? new Date(a._created_at).getTime() : 0;
      const tb = b._created_at ? new Date(b._created_at).getTime() : 0;
      if (tb !== ta) return tb - ta;
      return (b._broker_id_num || 0) - (a._broker_id_num || 0);
    });

    return arr;
  }, [
    formData.labRecords,
    formData.ipdRecords,
    formData.opdRecords,
    formData.pharmacyRecords,
    formData.brokers,
    formData.broker_id,
    formData.search_key,
    brokerMetaById,
  ]);

  // ✅ Updated totals to include totalPaid (commission + paid + due)
  const totals = useMemo(() => {
    let c = 0, p = 0, d = 0;
    aggregated.forEach((b) => {
      c += b.lab.commission + b.ipd.commission + b.opd.commission + b.pharmacy.commission;
      p += b.lab.paid + b.ipd.paid + b.opd.paid + b.pharmacy.paid;
      d += b.lab.due + b.ipd.due + b.opd.due + b.pharmacy.due;
    });
    return { c: c.toFixed(2), p: p.toFixed(2), d: d.toFixed(2) };
  }, [aggregated]);

  const openPayModal = (broker, dept) => {
    const sec = broker[dept];
    if (!sec || sec.due <= 0.01) return;
    setPayContext({ dept, broker, rows: sec.rows, commission: sec.commission, paid: sec.paid, due: sec.due });
    setPayAmount(sec.due.toFixed(2));
    setPayError("");
    setPayModalOpen(true);
  };

  const closePayModal = () => {
    setPayModalOpen(false);
    setPayContext(null);
    setPayAmount("");
    setPayError("");
  };

  const handlePaySubmit = async (e) => {
    e.preventDefault();
    if (!payContext) return;
    let amt = parseFloat(payAmount || 0);
    if (isNaN(amt) || amt <= 0) {
      setPayError("Please enter a valid amount.");
      return;
    }

    const { dept, rows } = payContext;
    const sorted = [...rows]
      .map((r) => {
        let info;
        if (dept === "lab") info = readLabCommissions(r);
        if (dept === "ipd") info = readIpdCommissions(r);
        if (dept === "opd") info = readOpdCommissions(r);
        if (dept === "pharmacy") info = readPharmCommissions(r);
        return { r, due: info.due };
      })
      .filter((x) => x.due > 0.01)
      .sort((a, b) => b.due - a.due);

    if (sorted.length === 0) {
      setPayError("No outstanding due for this department.");
      return;
    }

    setPayProcessing(true);
    setPayError("");

    try {
      for (const { r, due } of sorted) {
        if (amt <= 0.0001) break;
        const payNow = Math.min(amt, due);
        amt -= payNow;

        const fd = new FormData();
        let url = "";
        if (dept === "lab") {
          fd.append("lab_reg_id", r.lab_reg_id);
          fd.append("lab_reg_broker_id", r.lab_reg_broker_id || "");
          fd.append("pay_amount", payNow.toFixed(2));
          url = AppConst.BASE_URL + AppConst.payBrokerCommissionUrl;
        } else if (dept === "ipd") {
          fd.append("ipd_id", r.ipd_id);
          fd.append(
            "broker_id",
            r.ipd_bill_broker_id || r.broker_id || r.broker_user_id || ""
          );
          fd.append("pay_amount", payNow.toFixed(2));
          url =
            AppConst.BASE_URL +
            (AppConst.payIpdBrokerCommissionUrl || "/api/pay_ipd_broker_commission");
        } else if (dept === "opd") {
          fd.append("opd_id", r.opd_id);
          fd.append("opd_broker_id", r.opd_broker_id || "");
          fd.append("pay_amount", payNow.toFixed(2));
          url = AppConst.BASE_URL + AppConst.payBrokerCommissionOpdUrl;
        } else if (dept === "pharmacy") {
          fd.append("med_bill_id", r.med_bill_id);
          fd.append("med_bill_broker_id", r.med_bill_broker_id || "");
          fd.append("pay_amount", payNow.toFixed(2));
          url = AppConst.BASE_URL + AppConst.payBrokerCommissionPharmacyUrl;
        }

        const res = await axios.post(url, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (!res.data?.status) {
          throw new Error(res.data?.message || "Failed to save a payment");
        }
      }

      toast.success("Payment saved successfully.");
      closePayModal();
      fetchAll();
    } catch (err) {
      console.error("pay error", err);
      setPayError(err.response?.data?.message || err.message || "Failed to save payment.");
    } finally {
      setPayProcessing(false);
    }
  };

  const renderDeptCell = (broker, dept, label) => {
    const sec = broker[dept];
    const disabled = sec.due <= 0.01;
    return (
      <div style={{ fontSize: "0.85rem", lineHeight: 1.3 }}>
        <div>
          <strong className="text-primary">₹{sec.commission.toFixed(2)}</strong>
        </div>
        <div className="text-muted">Paid: ₹{sec.paid.toFixed(2)}</div>
        <div className={sec.due > 0 ? "text-danger fw-bold" : "text-muted"}>
          Due: ₹{sec.due.toFixed(2)}
        </div>
        {sec.commission > 0 && (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary mt-1"
            style={{ padding: "2px 8px", fontSize: "0.75rem" }}
            onClick={() => openPayModal(broker, dept)}
            disabled={disabled}
          >
            {disabled ? "Paid" : "Pay"}
          </button>
        )}
      </div>
    );
  };

  const columns = useMemo(
    () => [
      { name: "SL", selector: (_r, i) => i + 1, width: "60px" },
      {
        name: "Broker",
        selector: (r) => r.broker_name,
        sortable: true,
        wrap: true,
        cell: (r) => (
          <div>
            <div className="fw-bold">{r.broker_name}</div>
            {r.broker_mobile && (
              <small className="text-muted">{r.broker_mobile}</small>
            )}
          </div>
        ),
        width: "200px",
      },
      { name: "Lab", cell: (r) => renderDeptCell(r, "lab", "Lab"), width: "150px" },
      { name: "IPD", cell: (r) => renderDeptCell(r, "ipd", "IPD"), width: "150px" },
      { name: "OPD", cell: (r) => renderDeptCell(r, "opd", "OPD"), width: "150px" },
      {
        name: "Pharmacy",
        cell: (r) => renderDeptCell(r, "pharmacy", "Pharmacy"),
        width: "150px",
      },
      {
        name: "Total Commission",
        selector: (r) =>
          r.lab.commission + r.ipd.commission + r.opd.commission + r.pharmacy.commission,
        sortable: true,
        right: true,
        cell: (r) => {
          const total =
            r.lab.commission + r.ipd.commission + r.opd.commission + r.pharmacy.commission;
          return <span className="fw-bold">₹{total.toFixed(2)}</span>;
        },
        width: "140px",
      },
      // ✅ NEW: Total Paid column (sum of paid across all departments)
      {
        name: "Total Paid",
        selector: (r) => r.lab.paid + r.ipd.paid + r.opd.paid + r.pharmacy.paid,
        sortable: true,
        right: true,
        cell: (r) => {
          const totalPaid = r.lab.paid + r.ipd.paid + r.opd.paid + r.pharmacy.paid;
          const totalCommission =
            r.lab.commission + r.ipd.commission + r.opd.commission + r.pharmacy.commission;
          // ✅ If paid > commission, highlight as overpaid (e.g. orange/warning color)
          const isOverpaid = totalPaid > totalCommission + 0.01;
          return (
            <div style={{ textAlign: "right" }}>
              <span className={isOverpaid ? "text-warning fw-bold" : "fw-bold text-success"}>
                ₹{totalPaid.toFixed(2)}
              </span>
              {isOverpaid && (
                <div>
                  <small className="text-warning" style={{ fontSize: "0.7rem" }}>
                    Overpaid +₹{(totalPaid - totalCommission).toFixed(2)}
                  </small>
                </div>
              )}
            </div>
          );
        },
        width: "140px",
      },
      {
        name: "Total Due",
        selector: (r) => r.lab.due + r.ipd.due + r.opd.due + r.pharmacy.due,
        sortable: true,
        right: true,
        cell: (r) => {
          const total = r.lab.due + r.ipd.due + r.opd.due + r.pharmacy.due;
          return (
            <span className={total > 0 ? "text-danger fw-bold" : ""}>
              ₹{total.toFixed(2)}
            </span>
          );
        },
        width: "120px",
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: "#f1f5fb",
        color: "#405189",
        fontSize: "14px",
        fontWeight: 600,
      },
    },
    rows: { style: { fontSize: "14px", minHeight: "90px" } },
    cells: {
      style: {
        paddingTop: "8px",
        paddingBottom: "8px",
        paddingLeft: "10px",
        paddingRight: "10px",
      },
    },
  };

  const reactSelectStyles = {
    control: (base) => ({ ...base, minHeight: "38px", fontSize: "14px" }),
    option: (base) => ({ ...base, fontSize: "14px" }),
  };

  const handleExportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(16);
    doc.setFont(undefined, "bold");
    doc.text("All Broker Commission Report", pageWidth / 2, 15, { align: "center" });

    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    doc.text(
      `Period: ${formatDate(formData.from)} to ${formatDate(formData.to)}`,
      pageWidth / 2,
      22,
      { align: "center" }
    );

    const body = aggregated.map((r, i) => {
      const total =
        r.lab.commission + r.ipd.commission + r.opd.commission + r.pharmacy.commission;
      const totalPaid = r.lab.paid + r.ipd.paid + r.opd.paid + r.pharmacy.paid;
      const totalDue = r.lab.due + r.ipd.due + r.opd.due + r.pharmacy.due;
      const isOverpaid = totalPaid > total + 0.01;
      return [
        i + 1,
        `${r.broker_name}${r.broker_mobile ? ` (${r.broker_mobile})` : ""}`,
        `${r.lab.commission.toFixed(2)} / ${r.lab.paid.toFixed(2)} / ${r.lab.due.toFixed(2)}`,
        `${r.ipd.commission.toFixed(2)} / ${r.ipd.paid.toFixed(2)} / ${r.ipd.due.toFixed(2)}`,
        `${r.opd.commission.toFixed(2)} / ${r.opd.paid.toFixed(2)} / ${r.opd.due.toFixed(2)}`,
        `${r.pharmacy.commission.toFixed(2)} / ${r.pharmacy.paid.toFixed(2)} / ${r.pharmacy.due.toFixed(2)}`,
        total.toFixed(2),
        // ✅ Total Paid column — annotate overpaid with "+"
        isOverpaid
          ? `${totalPaid.toFixed(2)} (+${(totalPaid - total).toFixed(2)})`
          : totalPaid.toFixed(2),
        totalDue.toFixed(2),
      ];
    });

    // ✅ Footer total row — added Total Paid (totals.p)
    body.push(["", "TOTAL", "", "", "", "", totals.c, totals.p, totals.d]);

    autoTable(doc, {
      startY: 28,
      head: [
        [
          "SL",
          "Broker",
          "Lab (C/P/D)",
          "IPD (C/P/D)",
          "OPD (C/P/D)",
          "Pharmacy (C/P/D)",
          "Total Comm.",
          "Total Paid",  // ✅ NEW
          "Total Due",
        ],
      ],
      body,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [64, 81, 137], textColor: 255, fontStyle: "bold" },
      didParseCell: (data) => {
        if (data.row.index === body.length - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [241, 245, 251];
        }
      },
    });

    doc.save(`All_Brokers_${getCurrentDate()}.pdf`);
  };

  return (
    <div>
      <div className="card-body border border-dashed border-end-0 border-start-0">
        <div className="row g-3 align-items-end">
          <div className="col-auto">
            <label className="form-label mb-1">From</label>
            <input
              type="date"
              className="form-control"
              name="from"
              onChange={handleChange}
              value={formData.from}
              style={{ width: "150px" }}
            />
          </div>

          <div className="col-auto">
            <label className="form-label mb-1">To</label>
            <input
              type="date"
              className="form-control"
              name="to"
              onChange={handleChange}
              value={formData.to}
              style={{ width: "150px" }}
            />
          </div>

          <div className="col-auto" style={{ minWidth: "260px" }}>
            <label className="form-label mb-1">Broker</label>
            <Select
              options={brokerOptions}
              value={
                brokerOptions.find(
                  (o) => String(o.value) === String(formData.broker_id)
                ) || null
              }
              onChange={(opt) => {
                if (!opt) {
                  setFormData((prev) => ({ ...prev, broker_id: "", broker_name: "" }));
                } else {
                  const cleanName = opt.label.split("—")[0].trim();
                  setFormData((prev) => ({
                    ...prev,
                    broker_id: String(opt.value),
                    broker_name: cleanName,
                  }));
                }
              }}
              onInputChange={(val, { action }) => {
                if (action === "input-change") loadBrokers(val);
              }}
              isClearable
              placeholder="All Brokers"
              styles={reactSelectStyles}
              isLoading={loadingBrokers}
            />
          </div>

          <div className="col" style={{ minWidth: "250px" }}>
            <label className="form-label mb-1">Search</label>
            <div className="search-box">
              <input
                className="form-control search"
                name="search_key"
                onChange={handleChange}
                value={formData.search_key}
                placeholder="Search by broker name or mobile..."
                type="text"
              />
              <i className="ri-search-line search-icon"></i>
            </div>
          </div>

          <div className="col-auto">
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-secondary" onClick={handleReset}>
                <i className="ri-refresh-line me-1"></i> Clear
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={handleExportPDF}
                disabled={aggregated.length === 0}
              >
                <i className="ri-file-pdf-line me-1"></i> Export PDF
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card-body pt-3">
        <DataTable
          columns={columns}
          data={aggregated}
          customStyles={customStyles}
          pagination
          highlightOnHover
          persistTableHead
          progressPending={loading}
          noDataComponent={<div className="text-muted py-3">No records found</div>}
        />

        {aggregated.length > 0 && (
          <div className="d-flex justify-content-end mt-3">
            <div className="text-muted" style={{ fontSize: "0.95rem" }}>
              <strong>Total Commission:</strong> ₹{totals.c} &nbsp;|&nbsp;
              <strong>Total Paid:</strong> ₹{totals.p} &nbsp;|&nbsp;
              <strong>Total Due:</strong> ₹{totals.d}
            </div>
          </div>
        )}
      </div>

      {payModalOpen && payContext && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.4)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form onSubmit={handlePaySubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">
                    Pay Broker Commission ({payContext.dept.toUpperCase()})
                  </h5>
                  <button type="button" className="btn-close" onClick={closePayModal} />
                </div>
                <div className="modal-body">
                  <p className="mb-2">
                    <strong>Broker:</strong> {payContext.broker.broker_name}{" "}
                    {payContext.broker.broker_mobile
                      ? `(${payContext.broker.broker_mobile})`
                      : ""}
                  </p>
                  <p className="mb-2">
                    <strong>Department:</strong>{" "}
                    {payContext.dept.charAt(0).toUpperCase() + payContext.dept.slice(1)}
                  </p>
                  <p className="mb-2">
                    <strong>Records involved:</strong> {payContext.rows.length}
                  </p>
                  <p className="mb-2">
                    <strong>Commission:</strong> ₹{payContext.commission.toFixed(2)}{" "}
                    &nbsp;|&nbsp; <strong>Paid:</strong> ₹{payContext.paid.toFixed(2)}{" "}
                    &nbsp;|&nbsp; <strong>Due:</strong> ₹{payContext.due.toFixed(2)}
                  </p>
                  <small className="text-muted d-block mb-3">
                    Amount will be distributed across {payContext.rows.length} record(s)
                    automatically (largest due first).
                  </small>

                  <div className="mb-3">
                    <label className="form-label">Pay Amount (₹)</label>
                    <input
                      type="number"
                      className="form-control"
                      step="0.01"
                      min="0"
                      value={payAmount}
                      onChange={(e) => setPayAmount(e.target.value)}
                    />
                  </div>

                  {payError && <div className="alert alert-danger py-2">{payError}</div>}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={closePayModal}
                    disabled={payProcessing}
                  >
                    Close
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={payProcessing}
                  >
                    {payProcessing ? "Saving..." : "Save Payment"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllBrokerReferral;